'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form/form-field';
import { MessageBar } from '@/components/ui/message-bar';
import { postWithRegionRetry } from '@/lib/auth/post-with-region-retry';
import styles from './forgot-password-form.module.css';

const ForgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
});

type ForgotPasswordValues = z.infer<typeof ForgotPasswordSchema>;

export function ForgotPasswordForm() {
  const router = useRouter();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordValues>({ resolver: zodResolver(ForgotPasswordSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);

    const response = await postWithRegionRetry('/web-api/auth/forgot-password', values);

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      setServerError(body.message ?? 'Unable to send reset email');
      return;
    }

    // Confirm and return to sign-in — do NOT route to /change-password.
    //
    // That is where this used to go, carrying only `?email=`. It put the
    // "set a new password" form in front of someone who had not yet proved
    // they own the mailbox, and it could not have worked anyway: the reset
    // code exists only in the emailed link, so /confirm-forgot-password
    // rejected every submission from that screen for an empty
    // `confirmationCode`. The new password is set from the emailed link and
    // nowhere else.
    //
    // Same wording and destination as the Angular app's ForgotPasswordPage,
    // which is the reference for this flow.
    toast.success('Password Reset!', {
      description: 'An email has been sent to your inbox with instructions to reset your password.',
    });
    router.push('/login');
  });

  return (
    <section className={styles.formBody}>
      <div className={styles.formTitle}>
        <h1 className={styles.formTitleHeading}>Forgot Password?</h1>
        <p className={styles.formSubtitle}>Enter your email to reset password</p>
      </div>

      <form onSubmit={onSubmit} noValidate className={styles.forgotPasswordForm}>
        <div className={styles.formInputs}>
          <FormField
            className={styles.formField}
            hasError={Boolean(errors.email)}
            error={errors.email?.message}
            label="Email address"
          >
            <input
              id="forgot-password-email"
              type="email"
              autoComplete="email"
              placeholder="Email address..."
              aria-label="Email address"
              className={styles.formInput}
              {...register('email')}
            />
          </FormField>
        </div>

        {serverError ? (
          <div role="alert" className={styles.serverError}>
            <MessageBar variant="negative" shape="note" icon="triangle-alert">
              {serverError}
            </MessageBar>
          </div>
        ) : null}

        <Button
          type="submit"
          size="medium"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          className={styles.forgotPasswordBtn}
        >
          {isSubmitting ? 'Sending email...' : 'Send email'}
        </Button>
      </form>

      <a className={styles.backLink} href="/login">
        Back
      </a>
    </section>
  );
}
